import { faRemove } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Button, CardV2 as Card, Chip, EmptyState, Well } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';

import * as styles from './SelectedFilters.css';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function SelectedFilterButton({
  targeting,
  removeTargeting,
  name,
}: {
  targeting: Targeting;
  removeTargeting: (targeting: Targeting) => void;
  name: string;
}): JSX.Element {
  function handleRemoveTargeting(): void {
    removeTargeting(targeting);
  }

  return (
    <Chip
      className={styles.chipFix}
      suffix={
        <Button onClick={handleRemoveTargeting} type={'naked'}>
          <FontAwesomeIcon className={'icon'} icon={faRemove} />
        </Button>
      }
      theme={'sky'}
    >
      {name}
    </Chip>
  );
}

function SelectedFilters({ targetings }: Readonly<IProps>): JSX.Element {
  const { Targetings } = useStore();
  const { t } = useTranslation();

  function removeTargeting(targeting: Targeting): void {
    if (!targeting.isEphemeral) {
      targeting.set({ is_archived: true });
    } else {
      targetings.remove(targeting);
      Targetings.remove(targeting.id);
    }
  }

  const filteredTargetings = targetings.filter(
    (t) => !t.get('is_archived') && t.get('kind') === 'geo',
  );
  const filteredTargetingsWithName = filteredTargetings.filter((t) => !!t.get('name'));
  const numFilters = filteredTargetingsWithName.length;

  // TODO: make this into something more global?
  const selectedKeywordButtons = filteredTargetingsWithName.map((targeting) => {
    return (
      <SelectedFilterButton
        key={targeting.id}
        name={targeting.get('name')}
        removeTargeting={removeTargeting}
        targeting={targeting}
      />
    );
  });

  function handleClearAllFilters(): void {
    filteredTargetings.forEach((targeting) => {
      removeTargeting(targeting);
    });
  }

  return (
    <Card contentClassName={styles.root} isSticky={true} width={'full'}>
      <Card.Header title={t('Selected Filters')} />
      <Card.Content addVerticalGap={true}>
        {numFilters === 0 ? (
          <EmptyState
            description={t('Get started by selecting some filters')}
            label={t('No filters selected')}
            theme={'slate'}
          />
        ) : (
          <>
            <Well
              className={styles.selectedFilters}
              description={numFilters > 1 && t('any of the selected')}
              label={t('Locations')}
              layout={'vertical'}
            >
              <Flex gap={cssVar('--spacing-2')} wrap={'wrap'}>
                {selectedKeywordButtons}
              </Flex>
            </Well>
            {/* TODO: pull actual age demographics */}
            <Well
              // description={numFilters > 1 && t('any of the selected')}
              className={styles.selectedFilters}
              label={t('Age Demographics')}
              layout={'vertical'}
            >
              <Flex gap={cssVar('--spacing-2')} wrap={'wrap'}>
                <Chip
                  className={styles.chipFix}
                  suffix={<FontAwesomeIcon icon={faRemove} />}
                  theme={'sky'}
                >
                  25-34 years
                </Chip>
              </Flex>
            </Well>
            {/* TODO: pull actual interests */}
            <Well
              // description={numFilters > 1 && t('any of the selected')}
              className={styles.selectedFilters}
              label={t('Interests')}
              layout={'vertical'}
            >
              <Flex gap={cssVar('--spacing-2')} wrap={'wrap'}>
                <Chip
                  className={styles.chipFix}
                  suffix={<FontAwesomeIcon icon={faRemove} />}
                  theme={'sky'}
                >
                  Climate Action Supporters
                </Chip>
              </Flex>
            </Well>
            <Button onClick={handleClearAllFilters}>{t('Clear filters')}</Button>
          </>
        )}
      </Card.Content>
    </Card>
  );
}

export default observer(SelectedFilters);
